import { getSolidDataset, SolidDataset, ThingLocal } from '@inrupt/solid-client'
import log from 'loglevel'
import { DateTime } from 'luxon'
import React from 'react'
import { useHistory } from 'react-router-dom'
import { v4 } from 'uuid'
import { fetchByServer, submitResponseToServer } from '../api/aggregationApi'
import { Box } from '../components/Box'
import { Button } from '../components/Button'
import { Settings } from '../components/Settings'
import { Spinner } from '../components/Spinner'
import { SurveyQuestion } from '../components/survey/SurveyQuestion'
import { useCookie } from '../hooks/useCookie'
import { useQuery } from '../hooks/useQuery'
import { useSaveToPod } from '../hooks/useSaveToPod'
import { createOpenAnswer } from '../models/Answer'
import { ButtonState } from '../models/ButtonState'
import { createCompletedQuestion } from '../models/CompletedQuestion'
import { Question } from '../models/Question'
import { createSurveyCompletionTask } from '../models/SurveyCompletionTask'
import { responseReducer } from '../reducers/responseReducer'
import { updateDataset } from '../utils/SolidUpdater'
import { getQuestionsFromDataSet } from '../utils/surveyUtils'
import { upsertSolidDataSet } from '../utils/upsertSolidDataSet'

export function Survey() {
  const history = useHistory()
  const query = useQuery()
  const [surveyDataset, setSurveyDataset] = React.useState<
    SolidDataset | undefined
  >(undefined)
  const [containerUrl] = useCookie('containerUrl')

  const [status, setStatus] = React.useState<ButtonState>('idle')
  const [errMsg, setErrMsg] = React.useState('')
  const [surveyUrl, setSurveyUrl] = React.useState<string>(
    query.get('link') || ''
  )
  const [questions, setQuestions] = React.useState<Question[]>([])
  const [responseState, dispatch] = React.useReducer(responseReducer, {
    answers: [],
    surveyUrl: '',
    containerUrl: containerUrl,
    surveyStartedAt: DateTime.now(),
  })

  const { status: saveToPodStatus, handleSaveToPod } = useSaveToPod()
  const [
    submitResponseStatus,
    setSubmitResponseStatus,
  ] = React.useState<ButtonState>('idle')

  /**
   * Based on survey ontology:
   * https://cefriel.github.io/survey-ontology/docs/index.html
   *
   * 1. create answers from local data
   * 2. create 'completedQuestion' for each answer (use updated from local answer data)
   * 3. create 'surveyCompletionTask'
   *
   * TODO: instead of locally saving the response to own pod, and afterwards submitting to server,
   * Do everything serverside (make FE solid agnostic?)
   */
  async function handleSubmitSurvey(): Promise<void> {
    setSubmitResponseStatus('pending')
    const newId = v4()
    let fileName: string
    let responseDataset: SolidDataset
    try {
      const initResponse = await createResponseDataSet(newId, containerUrl)
      fileName = initResponse.fileName
      responseDataset = initResponse.responseDataset
    } catch (e) {
      log.error({ error: e, msg: 'Could not initialize response dataset' })
      setSubmitResponseStatus('rejected')
      return
    }

    const answerThings: ThingLocal[] = []
    const completeTaskThings: ThingLocal[] = []
    responseState.answers.forEach(a => {
      const answerThing = createOpenAnswer(a.answerText)
      answerThings.push(answerThing)
      completeTaskThings.push(
        createCompletedQuestion(
          a.answerText,
          a.lastUpdated,
          a.questionUrl,
          answerThing
        )
      )
    })
    const surveyCompletionTaskThing = createSurveyCompletionTask(
      newId,
      responseState.surveyStartedAt,
      DateTime.now()
    )

    const updatedDataset = updateDataset(responseDataset, [
      ...answerThings,
      ...completeTaskThings,
      surveyCompletionTaskThing,
    ])

    return handleSaveToPod(updatedDataset).then(savedDataSet => {
      return submitResponseToServer(
        savedDataSet,
        responseState.surveyUrl,
        newId
      ).then(
        () => {
          setSubmitResponseStatus('resolved')
          history.push(`/submitted?fileName=${fileName}`)
        },
        e => {
          log.error({ err: e })
          setSubmitResponseStatus('rejected')
        }
      )
    })
  }

  function createResponseDataSet(
    responseId: string,
    containerUri: string
  ): Promise<{ fileName: string; responseDataset: SolidDataset }> {
    const fileName = `response-${responseId}.ttl`
    return upsertSolidDataSet(
      { containerUri, fileName },
      { fetch: fetchByServer }
    ).then(responseDataset => {
      return { fileName, responseDataset }
    })
  }

  React.useEffect(() => {
    if (!surveyUrl) {
      setStatus('idle')
      return
    }

    setStatus('pending')
    getSolidDataset(surveyUrl, { fetch: fetchByServer }).then(
      dataset => {
        setSurveyDataset(dataset)
        setStatus('resolved')
      },
      e => {
        setErrMsg(
          `Could not read data from URL ${
            e.statusCode ? `[${e.statusCode}]` : ''
          }`
        )
        setStatus('rejected')
      }
    )
  }, [surveyUrl])

  React.useEffect(() => {
    if (!surveyDataset) return
    const { survey, questions } = getQuestionsFromDataSet(surveyDataset)
    setQuestions(questions)
    if (survey) {
      dispatch({
        type: 'setSurveyUrl',
        surveyUrl: survey.url,
      })
    }
  }, [surveyDataset])

  React.useEffect(() => {
    log.debug({ saveToPodStatus })
  }, [saveToPodStatus])

  const Placeholder = () => (
    <div className="text-center mb-4 font-extrabold tracking-tight">
      <h2 className="text-lg font-light text-gray-500">No survey found</h2>
    </div>
  )

  return (
    <>
      <Settings className="mb-8">
        <div className="px-4 py-5 sm:px-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 fill-current text-gray-600"
              >
                <path d="M16.32 14.9l1.1 1.1c.4-.02.83.13 1.14.44l3 3a1.5 1.5 0 0 1-2.12 2.12l-3-3a1.5 1.5 0 0 1-.44-1.14l-1.1-1.1a8 8 0 1 1 1.41-1.41l.01-.01zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z"></path>
              </svg>
            </div>
            <input
              placeholder="Enter a Survey URL"
              value={surveyUrl}
              onChange={e => setSurveyUrl(e.target.value)}
              className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-500 rounded py-3 px-4 mb-3 mt-3 leading-tight pl-10 pr-4 py-2"
            />
          </div>

          {status === 'rejected' ? (
            <div className="text-red-600 text-left text-xs font-light text-gray-400">
              {errMsg}
            </div>
          ) : null}
          {status === 'resolved' ? (
            <div className="text-green-600 text-left text-xs font-light text-gray-400">
              Survey loaded
            </div>
          ) : null}
        </div>
      </Settings>

      {status === 'idle' || status === 'rejected' ? <Placeholder /> : null}
      {status === 'pending' ? (
        <div className="flex flex-col items-center">
          <Spinner />
        </div>
      ) : null}
      {status === 'resolved' ? (
        <Box
          className="mb-8"
          disabled={
            saveToPodStatus === 'pending' || submitResponseStatus === 'pending'
          }
        >
          {questions.map((q, index) => (
            <SurveyQuestion
              key={q.url}
              index={index}
              question={q}
              disabled={saveToPodStatus === 'pending'}
              handleOnChange={e =>
                dispatch({
                  type: 'updateAnswerText',
                  text: e.target.value,
                  questionUrl: q.url,
                })
              }
            />
          ))}

          <Button
            onClick={handleSubmitSurvey}
            loading={
              saveToPodStatus === 'pending' ||
              submitResponseStatus === 'pending'
            }
          >
            Submit Survey
          </Button>
        </Box>
      ) : null}
    </>
  )
}
